import { AnalyticsHelperService } from './../../services/analytics-helper.service';
import { AnalyticsService } from '@app/shared/modules/analytics/services/analytics.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import { AssementDay } from './../../../../shared/modules/evc-api/models/create-quote.dto';
import { LanguageService } from './../../../../core/language/services/language.service';
import { SmallBizStoreService } from './../../stores/small-biz-store.service';
import { EvcSmallBizQuoteHelper } from './../../services/quote-helper.service';
import { minDateValidator } from './../../libs/validators/minimum-date';
import { ButtonStateEnum } from './../../libs/component-lib/enums/button-state.enum';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { EvsbVirtualCheckupModel } from './evsb-virtual-checkup.model';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { EvsbVirtualCheckupMapper } from './evsb-virtual-checkup.mapper';
import { atleastOne } from '../../libs/validators/atleast-one';
import { EvcApiService } from '@app/shared/modules/evc-api/services/evc-api.service';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  EmailValidator,
  normalizePhoneNumber,
  normalizeVATNumber,
  PhoneValidator,
  VatValidator,
} from '@springbox/validators';
import { PartnerService } from 'src/app/modules/evc-quote/services/partner/partner.service';
import { PartnerUI } from '@app/shared/modules/evc-api/models/partner-ui.interface';

@Component({
  selector: 'evsb-virtual-checkup',
  templateUrl: './evsb-virtual-checkup.component.html',
  styleUrls: ['./evsb-virtual-checkup.component.scss'],
})
export class EvsbVirtualCheckupComponent implements OnInit, OnDestroy {
  @ViewChild('stepOne') stepOne;
  @ViewChild('stepTwo') stepTwo;

  public model: EvsbVirtualCheckupModel;
  public form: UntypedFormGroup;
  public formSubmitted = false;

  private partner: PartnerUI;

  public get tomorrow(): string {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  }

  private notifier = new Subject<void>();

  constructor(
    private mapper: EvsbVirtualCheckupMapper,
    private fb: UntypedFormBuilder,
    private evcSmallBizQuoteHelper: EvcSmallBizQuoteHelper,
    private store: SmallBizStoreService,
    private languageService: LanguageService,
    private evcApiService: EvcApiService,
    private router: Router,
    private modal: NgbModal,
    private analyticsService: AnalyticsService,
    private analyticsHelperService: AnalyticsHelperService,
    private title: Title,
    private translate: TranslateService,
    private partnerService: PartnerService
  ) {
    this.translate.onLangChange.pipe(takeUntil(this.notifier)).subscribe(() => {
      this.title.setTitle(this.translate.instant('modules.evsb.pages.title.virtualCheckup'));
    });

    this.partnerService.restoredPartner$.subscribe((partner: PartnerUI) => {
      if (partner) {
        this.partner = partner;
      }
    });
  }

  ngOnInit(): void {
    this.model = this.mapper.getModel();
    this.form = this.buildForm();

    this.analyticsService.push({
      event: 'b-to-b-ev-simulator-flow',
      eventCategory: 'b-to-b-ev-simulator',
      eventAction: 'step-virtual-assesment',
      eventLabel: 'chose-days',
    });

    this.analyticsService.push({
      event: 'b-to-b-ev-simulator-flow',
      eventCategory: 'b-to-b-ev-simulator',
      eventAction: 'step-virtual-assesment',
      eventLabel: 'contact-details',
    });
  }

  ngOnDestroy(): void {
    this.notifier.next();
    this.notifier.complete();
  }

  public get step2ButtonEnabled(): boolean {
    return !!(this.form.get('day1').value && this.form.get('day2').value && this.form.get('day3').value);
  }

  public get gotoStep2ButtonState(): ButtonStateEnum {
    if (this.step2ButtonEnabled) {
      return ButtonStateEnum.enabled;
    }

    return ButtonStateEnum.disabled;
  }

  public scrollToElement(element: HTMLElement | undefined) {
    element?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
  }

  public async submit() {
    this.formSubmitted = true;
    if (this.form.valid) {
      const sendOfferOnly = false;
      const createQuoteDto = await this.evcSmallBizQuoteHelper.convertSimulationToQuote(
        {
          vatNumber: normalizeVATNumber(this.form.get('vatNumber').value),
          company: this.form.get('company').value,
          firstName: this.form.get('firstName').value,
          lastName: this.form.get('lastName').value,
          telephone: normalizePhoneNumber(this.form.get('telephone').value),
          email: this.form.get('email').value,
          alreadyClient: this.form.get('alreadyClient').value,
        },
        this.store.model,
        this.getAssesment(),
        sendOfferOnly,
        this.partner,
        this.languageService.getLanguage()
      );

      // TODO: navigate to loading and then to success page
      this.evcApiService.createQuote(createQuoteDto).subscribe(
        (result) => {
          this.modal.dismissAll();
          this.router.navigate(['/thank-you'], { skipLocationChange: true });
          this.analyticsHelperService.analyticsWorkflowStep('contact-request-submit', {
            event: 'b-to-b-ev-simulator-contact-request-submit',
            withResult: true,
            leadId: result.id,
          });
        },
        (error) => {
          alert('Something went wrong. Please try again.');
          console.error(error);
        }
      );
    }
  }

  public buildForm(): UntypedFormGroup {
    return this.fb.group({
      day1: this.fb.control(this.model.day1, [Validators.required, minDateValidator(new Date())]),
      day1Options: this.fb.control(this.model.day1Options, [atleastOne]),
      day2: this.fb.control(this.model.day2, [Validators.required]),
      day2Options: this.fb.control(this.model.day2Options, [atleastOne]),
      day3: this.fb.control(this.model.day3, [Validators.required]),
      day3Options: this.fb.control(this.model.day3Options, [atleastOne]),
      vatNumber: this.fb.control(this.model.vatNumber, [Validators.required, VatValidator.validate]),
      company: this.fb.control(this.model.company, [Validators.required]),
      firstName: this.fb.control(this.model.firstName, [Validators.required]),
      lastName: this.fb.control(this.model.lastName, [Validators.required]),
      telephone: this.fb.control(this.model.telephone, [Validators.required, PhoneValidator.validate]),
      email: this.fb.control(this.model.email, [Validators.required, EmailValidator.validate]),
      alreadyClient: this.fb.control(this.model.alreadyClient, []),
      consent: this.fb.control(this.model.consent, [Validators.requiredTrue]),
    });
  }

  public normalizeVatNumber() {
    const value = this.form.get('vatNumber').value;
    this.form.get('vatNumber').setValue(normalizeVATNumber(value), { onlySelf: true, emitEvent: false });
  }

  public normalizePhoneNumber() {
    const value = this.form.get('telephone').value;
    this.form.get('telephone').setValue(normalizePhoneNumber(value), { onlySelf: true, emitEvent: false });
  }

  public invalid(name: string): boolean {
    return this.form.get(name).invalid && (this.form.get(name).dirty || this.formSubmitted);
  }

  private getAssesment(): AssementDay[] {
    const assesment: AssementDay[] = [];

    if (this.form.get('day1').value) {
      assesment.push({
        date: this.form.get('day1').value,
        timeframe: {
          ...this.form.get('day1Options').value,
        },
      });
    }

    if (this.form.get('day2').value) {
      assesment.push({
        date: this.form.get('day2').value,
        timeframe: {
          ...this.form.get('day2Options').value,
        },
      });
    }

    if (this.form.get('day3').value) {
      assesment.push({
        date: this.form.get('day3').value,
        timeframe: {
          ...this.form.get('day3Options').value,
        },
      });
    }

    return assesment;
  }
}
